"use client";

import { Button, buttonVariants } from "@/components/ui/button";
import {
    AudioLines,
    Captions,
    Download,
    LoaderCircle,
    PlayCircle,
} from "lucide-react";
import { useState } from "react";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { usePayments } from "@/features/payments/hooks/usePayments";
import { Dubbings } from "@/app/api/[[...route]]/services/ai/ai-video-workflow/types";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SrclangType, getPlatformLanguages } from "@/db/schema";
import { cn } from "@/lib/utils";
import { t, getLanguageLabel } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import { GetVideoResponseType } from "../../../hooks/useGetVideo";

export default function DownloadButton({
    videoId,
    videoTitle,
    originalFileFormat,
    dubbings,
    captions,
}: {
    videoId: string;
    videoTitle: string | null;
    originalFileFormat: string | null;
    dubbings: Dubbings[];
    captions: GetVideoResponseType["data"]["captions"];
}) {
    const { dir } = useLocale();
    const [loading, setLoading] = useState(false);
    const setPaymentModalOpen = usePayments(
        (state) => state.setPaymentModalOpen
    );

    const audioLanguages: SrclangType[] = [];
    for (const ds of dubbings) {
        audioLanguages.push(...ds.languages);
    }

    const download = async (lang?: SrclangType) => {
        setLoading(true);

        const res = await client.api.storage.video[":videoId"].download.$get({
            param: { videoId },
            query: { lang },
        });

        if (!res.ok) {
            setLoading(false);

            if (res.status === 402) {
                setPaymentModalOpen(true, "PaymentWall");
            } else {
                toast.error(t("toast.error.default"), {
                    keyName: "toast.error.default",
                });
            }

            return;
        }

        const data = await res.json();
        const { downloadUrl } = data.data;

        try {
            // Open the download in a new tab. Keep the Referer: the CDN zone
            // blocks referrer-less requests (blockNoneReferrer), so
            // "noreferrer" here would turn every download into a 403.
            window.open(downloadUrl, "_blank", "noopener");

            toast.success(t("toast.success.download-started"), {
                keyName: "toast.success.download-started",
            });
        } catch (error) {
            console.error("Download error:", error);
            toast.error(t("toast.error.download-failed"), {
                keyName: "toast.error.download-failed",
            });
        } finally {
            setLoading(false);
        }
    };

    const downloadCaptions = async (captionsId: string, label: string) => {
        setLoading(true);

        try {
            const res = await client.api.storage.captions[
                ":captionsId"
            ].$get({ param: { captionsId } });

            if (!res.ok) {
                throw new Error("Failed to fetch captions");
            }

            const { data } = await res.json();

            if (!data?.srt) {
                throw new Error("No srt");
            }

            const blob = new Blob([data.srt], {
                type: "text/plain;charset=utf-8;",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const baseName = videoTitle?.trim()
                ? `${videoTitle.trim()} - ${label}`
                : label;
            link.setAttribute("download", `${baseName}.srt`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast.success(t("toast.success.download-captions"), {
                keyName: "toast.success.download-captions",
            });
        } catch (error) {
            console.error("Captions download error:", error);
            toast.error(t("toast.error.download-captions"), {
                keyName: "toast.error.download-captions",
            });
        } finally {
            setLoading(false);
        }
    };

    const platformLanguages = getPlatformLanguages();

    return (
        <DropdownMenu dir={dir}>
            <DropdownMenuTrigger
                className={cn(
                    buttonVariants({ variant: "secondary", size: "sm" })
                )}
            >
                {loading ? (
                    <div className="w-[58px] flex justify-center items-center">
                        <LoaderCircle className="size-4 animate-spin" />
                    </div>
                ) : (
                    <>
                        <Download className="size-3.5 me-2" />
                        {t("video-sheet.header.download-btn", undefined, undefined)}
                    </>
                )}
            </DropdownMenuTrigger>
            <DropdownMenuContent>
                <DropdownMenuLabel>
                    {t("video-sheet.download-menu.label")}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => download()} disabled={loading}>
                    <PlayCircle className="size-4" />
                    {t("video-sheet.download-menu.original-video")}
                </DropdownMenuItem>
                {audioLanguages.length > 0 && <DropdownMenuSeparator />}
                {audioLanguages.map((lang) => (
                    <DropdownMenuItem
                        key={lang}
                        onClick={() => download(lang)}
                        disabled={loading}
                    >
                        <AudioLines className="size-4" />
                        {t("video-sheet.download-menu.audio", undefined, {
                            language:
                                platformLanguages.find(
                                    (l) => l.code === lang
                                )?.label ?? "",
                        })}
                    </DropdownMenuItem>
                ))}
                {captions.length > 0 && <DropdownMenuSeparator />}
                {captions.map((caption) => (
                    <DropdownMenuItem
                        key={caption.id}
                        onClick={() =>
                            downloadCaptions(
                                caption.id,
                                getLanguageLabel(caption.srclang)
                            )
                        }
                        disabled={loading}
                    >
                        <Captions className="size-4" />
                        {t("video-sheet.download-menu.captions", undefined, {
                            language: getLanguageLabel(caption.srclang),
                        })}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
